import groq from "groq";
import { handleClientError } from "../../errors";
import { getClient } from "../sanity";
import { type BlogSimple, blogSimpleFragment } from "./blogs";
import { type Customer, customerFragment } from "./customers";

export type Banner = {
  title: string;
  description: string;
  ctaLink: string;
  ctaNewTab: boolean;
};

type SiteSettings = {
  announcementBanner?: Banner;
  defaultFeaturedBlogs?: BlogSimple[];
  logos?: Customer[];
  mobileLogos?: Customer[];
};

export const getAnnouncementBanner = async (): Promise<Banner | undefined> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      announcementBanner->{title, description, ctaLink, 'ctaNewTab': coalesce(ctaNewTab, false)},
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.announcementBanner;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch announcement banner: ${errorMessage}`);
    return undefined;
  }
};

export const getDefaultBlogs = async (): Promise<BlogSimple[]> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      defaultFeaturedBlogs[]->{ ${blogSimpleFragment} },
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.defaultFeaturedBlogs ?? [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch default blogs: ${errorMessage}`);
    return [];
  }
};

export const getDefaultLogos = async (): Promise<Customer[]> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      logos[]->{ ${customerFragment} },
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.logos ?? [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch default customer logos from site settings: ${errorMessage}`);
    return [];
  }
};

export const getDefaultMobileLogos = async (): Promise<Customer[]> => {
  const query = groq`
    *[_type == "siteSettings"][0] {
      mobileLogos[]->{ ${customerFragment} },
    }
  `;

  try {
    const siteSettings = await getClient().fetch<SiteSettings | undefined>(query);
    return siteSettings?.mobileLogos ?? [];
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    handleClientError(`Failed to fetch default mobile logos from site settings: ${errorMessage}`);
    return [];
  }
};
